import type { ConfigProps, MenuItem } from '../../configTypes';
import { FeaturedImageFragment } from '../fragments/FeaturedImage';
import { ContributorsFragment } from '../fragments/Contributors';
import { NavigationMenuItemFragment } from '../fragments/NavigationMenuItem';
import { resolvePostImage } from '../lib/resolvePostImage';
import transformSeo from '../transform/transformSEO';
import type {
  TransformedItemPost,
  TransformedCategory,
  PostsByCategoryResult,
} from '../types';
import { describeGraphQlErrors, hasErrorForField } from '../utils';
import { executeGraphQlRequestWithErrors } from './executeGraphQlRequest';

export type { TransformedCategory, PostsByCategoryResult };

// ---------------------------------------------------------------------------
// Types (file-local)
// ---------------------------------------------------------------------------

type CategoryContentNode = {
  databaseId: number;
  date: string;
  excerpt: string;
  title: string;
  uri: string;
  slug: string;
  featuredImage?: {
    node: {
      altText: string;
      publicId: string;
      sourceUrl: string;
    };
  };
  contributors?: {
    types?: Array<{
      key: string;
      value: Array<{ title?: string }>;
    }>;
  };
};

type CategoryChild = {
  name: string;
  slug?: string;
  uri?: string;
  contentNodes: {
    nodes: CategoryContentNode[];
  };
};

type CategoryParentNode = {
  name?: string;
  slug?: string;
  uri?: string;
  children?: {
    nodes: Array<{ name: string; slug?: string; uri?: string }>;
  };
};

type PostsByCategoryGraphQLData = {
  category?: {
    name?: string;
    description?: string;
    contentNodes?: { nodes: CategoryContentNode[] };
    children?: { nodes: CategoryChild[] };
    parent?: { node?: CategoryParentNode };
  };
  generalSettings?: Record<string, string>;
  headerMenuItems?: { nodes: MenuItem[] };
  footerMenuItems?: { nodes: MenuItem[] };
  auxiliaryMenuItems?: { nodes: MenuItem[] };
};

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const ContentNodeFragment = (postTypeGql: string): string => `
fragment ContentNodeFragment on ${postTypeGql} {
  databaseId
  date: customDate
  excerpt
  title
  uri
  slug
  ...FeaturedImageFragment
  ...ContributorsFragment
}
`;

export const postsByCategoryQuery = (postTypeGql: string): string => `
${FeaturedImageFragment(postTypeGql)}
${ContributorsFragment(postTypeGql)}
${NavigationMenuItemFragment()}
${ContentNodeFragment(postTypeGql)}
query PostsByCategory(
  $category: ID!
  $limit: Int!
  $headerMenuLocation: MenuLocationEnum
  $footerMenuLocation: MenuLocationEnum
  $auxiliaryMenuLocation: MenuLocationEnum
) {
  category(id: $category, idType: SLUG) {
    name
    description
    contentNodes(first: $limit) {
      nodes {
        id
        ...ContentNodeFragment
      }
    }
    children {
      nodes {
        name
        slug
        uri
        contentNodes(first: $limit) {
          nodes {
            id
            ...ContentNodeFragment
          }
        }
      }
    }
    parent {
      node {
        name
        slug
        uri
        children {
          nodes {
            name
            slug
            uri
          }
        }
      }
    }
  }
  generalSettings {
    title
    description
  }
  headerMenuItems: menuItems(where: { location: $headerMenuLocation }, first: 1000) {
    nodes { ...NavigationMenuItemFragment }
  }
  footerMenuItems: menuItems(where: { location: $footerMenuLocation }, first: 1000) {
    nodes { ...NavigationMenuItemFragment }
  }
  auxiliaryMenuItems: menuItems(where: { location: $auxiliaryMenuLocation }, first: 1000) {
    nodes { ...NavigationMenuItemFragment }
  }
}
`;

// ---------------------------------------------------------------------------
// Transform
// ---------------------------------------------------------------------------

function transformNode(
  node: CategoryContentNode,
  fallbackPostImage?: string,
): TransformedItemPost {
  const publicId = node.featuredImage?.node?.publicId;
  const srcUrl = node.featuredImage?.node?.sourceUrl;
  const writerType = node.contributors?.types?.find((t) => t.key === 'writer');
  const author = writerType?.value?.[0]?.title || undefined;
  return {
    id: String(node.databaseId),
    title: node.title || '',
    link: node.uri || '',
    image:
      resolvePostImage(publicId ?? '', {}, srcUrl, fallbackPostImage) ??
      undefined,
    imagealt: node.featuredImage?.node?.altText || '',
    content: node.excerpt || '',
    date: node.date || '',
    author,
  };
}

// ---------------------------------------------------------------------------
// Fetch
// ---------------------------------------------------------------------------

/**
 * Fetches all posts for a given category slug, including sub-category children,
 * menus, and transformed SEO data.  Used to power hub pages.
 *
 * Migrated from se-supersonic-middleware: src/queries/fetchPostsByCategory.ts
 *
 * @param config  - Site configuration (cmsUrl, postTypeGql, menu locations, etc.)
 * @param category - Category slug to query
 * @param limit   - Maximum number of top-level posts to return
 */
export async function fetchPostsByCategory(
  config: ConfigProps,
  category: string,
  limit: number,
): Promise<PostsByCategoryResult | null> {
  const {
    headerMenuLocation,
    footerMenuLocation,
    auxiliaryMenuLocation,
    cmsUrl,
    postTypeGql,
  } = config;

  const query = postsByCategoryQuery(postTypeGql);
  const variables = {
    category,
    limit,
    headerMenuLocation,
    footerMenuLocation,
    auxiliaryMenuLocation,
  };

  try {
    const { data, errors } =
      await executeGraphQlRequestWithErrors<PostsByCategoryGraphQLData>({
        cmsUrl,
        query,
        variables,
      });

    if (!data) throw new Error('No data returned for category');
    // Return null only when the category itself doesn't exist — an existing
    // category with zero posts is valid and should render an empty state.
    if (!data.category) {
      if (hasErrorForField(errors, 'category')) {
        throw new Error(
          `[fetchPostsByCategory] WordPress failed to resolve ${category}: ${describeGraphQlErrors(errors)}`,
        );
      }
      return null;
    }

    const posts = (data.category?.contentNodes?.nodes ?? []).map((node) =>
      transformNode(node, config.fallbackPostImage),
    );

    const children = (data.category?.children?.nodes ?? []).map(
      (child: CategoryChild): TransformedCategory => ({
        categoryName: child?.name ?? '',
        categorySlug: child?.slug,
        categoryUri: child?.uri,
        contentNodes: (child?.contentNodes?.nodes ?? []).map((node) =>
          transformNode(node, config.fallbackPostImage),
        ),
      }),
    );

    const parentNode = data.category?.parent?.node;
    const parentSlug = parentNode?.slug;
    const siblings = (parentNode?.children?.nodes ?? []).map(
      (s): TransformedCategory => ({
        categoryName: s.name ?? '',
        categorySlug: s.slug,
        categoryUri: s.uri,
        contentNodes: [],
      }),
    );

    const transformedSEO = transformSeo({
      generalSettings: data.generalSettings ?? {},
      config,
      uri: category,
    });

    return {
      generalSettings: data.generalSettings ?? {},
      headerMenuItems: data.headerMenuItems ?? { nodes: [] },
      footerMenuItems: data.footerMenuItems ?? { nodes: [] },
      auxiliaryMenuItems: data.auxiliaryMenuItems,
      posts,
      children,
      parentSlug,
      parentCategoryName: parentNode?.name,
      siblings,
      categoryName: data.category?.name,
      categoryDescription: data.category?.description,
      transformedSEO,
    };
  } catch (error) {
    if (process.env['NODE_ENV'] === 'development') {
      console.warn('[fetchPostsByCategory]', category, error);
    }
    throw error;
  }
}

export default fetchPostsByCategory;
